import { Stack, Typography, Box, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ShieldIcon from '@mui/icons-material/Shield';
import { AxisCard, NotCollected, AxisLoading, AxisError, CountRow } from './ProductAxisCard';

// #108 Phase 1 — 제품 축의 보안 카드. 데이터: GET /api/security/portfolio 를 제품 id 로 필터.
// 전사 화면(/security)과 같은 피드를 쓰므로 두 화면의 숫자가 어긋날 수 없다.

const SEVS = ['critical', 'high', 'medium', 'low'];
const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

function bandMeta(theme, b) {
  switch (b) {
    case 'critical': return { color: theme.palette.error.dark || '#b71c1c', label: 'Critical' };
    case 'high': return { color: theme.palette.error.main, label: 'High' };
    case 'medium': return { color: theme.palette.warning.main, label: 'Medium' };
    case 'low': return { color: theme.palette.info?.main || theme.palette.text.secondary, label: 'Low' };
    case 'clean': return { color: theme.palette.success.main, label: '취약점 없음' };
    case 'disabled': return { color: theme.palette.text.disabled, label: 'Dependabot 꺼짐' };
    default: return { color: theme.palette.text.disabled, label: '스냅샷 없음' };
  }
}

function sevColor(theme, s) {
  switch (s) {
    case 'critical': return theme.palette.error.dark || '#b71c1c';
    case 'high': return theme.palette.error.main;
    case 'medium': return theme.palette.warning.main;
    default: return theme.palette.info?.main || theme.palette.text.secondary;
  }
}

export default function ProductSecurityCard({ productId, state }) {
  const theme = useTheme();
  const { data, error, loading } = state;
  const row = data?.products?.find((p) => p.id === productId) || null;

  const card = (children, updatedAt) => (
    <AxisCard
      title="보안"
      icon={<ShieldIcon sx={{ fontSize: 19, color: 'text.secondary' }} />}
      source={`${productId}/security/vuln-snapshot.json`}
      sourceUrl={`https://github.com/rayrndsw/${productId}/blob/main/security/vuln-snapshot.json`}
      updatedAt={updatedAt}
    >
      {children}
    </AxisCard>
  );

  if (loading) return card(<AxisLoading />, null);
  if (error) return card(<AxisError message="보안 현황을 불러오지 못했습니다" hint={error} />, null);
  if (!row) {
    return card(
      <NotCollected reason={`이 제품이 보안 포트폴리오 집계에 없습니다. products.yaml 의 prd-* 목록에 있어야 집계됩니다.`} />,
      null,
    );
  }
  if (row.missing) {
    return card(
      <NotCollected reason={`${productId} 저장소에 security/vuln-snapshot.json 이 없습니다 — daily-vuln-scan 워크플로가 아직 돌지 않았을 수 있습니다.`} />,
      null,
    );
  }

  const bm = bandMeta(theme, row.band);
  const cur = row.current || {};

  return (
    <AxisCard
      title="보안"
      icon={<ShieldIcon sx={{ fontSize: 19, color: 'text.secondary' }} />}
      source={`${productId}/security/vuln-snapshot.json`}
      sourceUrl={`https://github.com/rayrndsw/${productId}/blob/main/security/vuln-snapshot.json`}
      updatedAt={row.updated_at ? String(row.updated_at).slice(0, 10) : null}
      action={
        <Chip size="small" label={bm.label}
          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: bm.color, color: '#fff' }} />
      }
    >
      <CountRow
        items={SEVS.map((s) => ({
          label: SEV_LABEL[s],
          value: cur[s] ?? 0,
          color: (cur[s] || 0) > 0 ? sevColor(theme, s) : undefined,
        }))}
      />
      <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>
        <span>전체 <b>{cur.total ?? 0}</b></span>
        {row.repo_count != null && <span>저장소 <b>{row.repo_count}</b></span>}
        {row.dependabot_enabled === false && (
          <Tooltip title="Dependabot 이 꺼져 있으면 0 은 '취약점 없음' 이 아니라 '보고 있지 않음' 입니다">
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 700 }}>Dependabot 꺼짐</Box>
          </Tooltip>
        )}
      </Stack>
    </AxisCard>
  );
}
